// Upgrade CTA helper — surfaces a friendly "Upgrade Plan" toast whenever the
// backend rejects a coach action with HTTP 402 (plan-limit, plan-feature, or
// trial-expired). Deep-links to the Pricing page with the recommended plan
// pre-highlighted.

import { toast } from "sonner";
import { formatApiErrorDetail } from "./api";

const PLAN_NAMES = {
  trial: "Free Trial",
  starter: "Starter",
  pro: "Pro",
  organization: "Organization",
  enterprise: "Enterprise",
};

const FALLBACK_CHAIN = {
  trial: "pro",
  starter: "pro",
  pro: "organization",
  organization: "enterprise",
  enterprise: null,
};

/** Return the next-best plan when the backend didn't include `recommended_plan`. */
export function getRecommendedPlan(currentPlan) {
  return FALLBACK_CHAIN[currentPlan || "trial"] || null;
}

/** Friendly label for the recommended plan ("Upgrade to Pro" etc.). */
export function planLabel(planId) {
  return PLAN_NAMES[planId] || (planId ? planId : "");
}

/** True if the error is a plan-limit / plan-feature / trial-expired 402. */
export function isPlanLimitError(err) {
  if (err?.response?.status !== 402) return false;
  const d = err?.response?.data?.detail;
  return !!(d && (d.plan_limit || d.plan_feature || d.locked || d.trial_expired));
}

/**
 * Show a sonner toast for a plan-limit / trial-expired 402.
 * If the error isn't a plan-limit one, falls back to a plain toast.error.
 *
 * @param {Error} err            Axios error (may be anything)
 * @param {Function} navigate    react-router navigate(...)
 * @param {Object}   [opts]
 * @param {string}   [opts.fallback] Fallback message when error has no detail
 * @param {string}   [opts.currentPlan] Override current plan when backend
 *                                       didn't include it (rare)
 * @returns {boolean} true if a plan-limit toast was shown, false if generic
 */
export function showPlanLimitToast(err, navigate, opts = {}) {
  const detail = err?.response?.data?.detail || null;
  const fallback = opts.fallback || "Something went wrong.";
  const isPlanLimit = isPlanLimitError(err);

  if (!isPlanLimit) {
    toast.error(formatApiErrorDetail(detail) || fallback);
    return false;
  }

  const currentPlan = detail?.current_plan || opts.currentPlan || "trial";
  const recommended =
    detail?.recommended_plan || getRecommendedPlan(currentPlan);
  const message =
    formatApiErrorDetail(detail) ||
    "You've reached your current plan limit. Upgrade your plan to continue adding teams or players.";

  // Free Early Access: no upgrade path yet — offer support contact instead.
  if (detail?.early_access) {
    const support = detail.support_email || "support@squadfee.com";
    toast.error(message, {
      duration: 9000,
      action: {
        label: "Contact support",
        onClick: () => {
          if (typeof window !== "undefined") {
            window.location.href = `mailto:${support}?subject=${encodeURIComponent("Squad Fee Early Access limit")}`;
          }
        },
      },
    });
    return true;
  }

  // Enterprise users have nowhere to upgrade — gently route to Settings.
  if (!recommended) {
    toast.error(message, {
      duration: 8000,
      action: {
        label: "Contact sales",
        onClick: () => {
          if (typeof navigate === "function") {
            navigate("/pricing?recommended=enterprise");
          } else if (typeof window !== "undefined") {
            window.location.href = "/pricing?recommended=enterprise";
          }
        },
      },
    });
    return true;
  }

  toast.error(message, {
    duration: 8000,
    action: {
      label: "Upgrade Plan",
      onClick: () => {
        const url = `/pricing?recommended=${encodeURIComponent(recommended)}`;
        if (typeof navigate === "function") {
          navigate(url);
        } else if (typeof window !== "undefined") {
          window.location.href = url;
        }
      },
    },
  });
  return true;
}
